import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Container, 
  Paper, 
  Typography, 
  Button, 
  Box,
  CircularProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { questionnaireApi, SessionCompletionData } from '../services/api';
import styles from './Completed.module.css';

const Completed: React.FC = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();
  const [completionData, setCompletionData] = useState<SessionCompletionData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (sessionId) {
      loadCompletionData();
    } else {
      navigate('/404', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const loadCompletionData = async () => {
    if (!sessionId) return;

    try {
      setLoading(true);
      const data = await questionnaireApi.getCompletionData(sessionId);
      setCompletionData(data);
      setLoading(false);
    } catch (err: any) {
      // If session is invalid or not found, redirect to not found page
      navigate('/404', { replace: true });
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" className={styles.container}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!completionData) {
    return null;
  }

  return (
    <Container maxWidth="sm" className={styles.container}>
      <Paper className={styles.paper} elevation={3}>
        <Box className={styles.logoContainer}>
          <img src="/favicon.svg" alt="Tibbbi Logo" className={styles.logo} />
        </Box>
        
        <Box className={styles.iconContainer}>
          <CheckCircleIcon className={styles.successIcon} />
        </Box>
        
        <Typography variant="h4" gutterBottom className={styles.title}>
          Thank You!
        </Typography>
        
        <Typography variant="body1" className={styles.message}>
          We appreciate you taking the time to answer all the questions. Your responses have been successfully submitted.
        </Typography>
        
        <Typography variant="body1" className={styles.message}>
          We've sent a message to the team at {completionData.organizationName}, and they will review your information and get back to you as soon as possible.
        </Typography>
        
        <Box mt={4}>
          <Typography variant="body2" color="textSecondary" className={styles.note}>
            If you have any urgent concerns, please contact your healthcare provider directly.
          </Typography>
        </Box>
        
        <Box mt={4}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate('/')}
            fullWidth
            size="large"
          >
            Return to Home
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default Completed;